import React, { useEffect, useRef } from 'react';
import { X, FileText, GraduationCap, ClipboardList, BookOpen, FileQuestion } from 'lucide-react';
import { DocumentType } from '../../types/document';
import './DocumentTypeModal.css';

interface DocumentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: DocumentType | null) => void;
  filename: string;
}

const documentTypeOptions = [
  {
    type: DocumentType.SYLLABUS,
    icon: FileText,
    label: 'Syllabus',
    description: 'Course syllabus or program outline'
  },
  {
    type: DocumentType.EXAM,
    icon: GraduationCap,
    label: 'Exam',
    description: 'Tests, quizzes, or examinations'
  },
  {
    type: DocumentType.ASSIGNMENT,
    icon: ClipboardList,
    label: 'Assignment',
    description: 'Homework, projects, or tasks'
  },
  {
    type: DocumentType.NOTES,
    icon: BookOpen,
    label: 'Notes',
    description: 'Personal notes or study materials'
  },
  {
    type: DocumentType.OTHER,
    icon: FileQuestion,
    label: 'Other',
    description: 'Any other educational materials'
  }
];

const DocumentTypeModal: React.FC<DocumentTypeModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  filename
}) => {
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter') {
        onSelect(null); // Skip selection
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onSelect]);

  // Focus first button when modal opens
  useEffect(() => {
    if (isOpen && firstButtonRef.current) {
      // Use setTimeout to ensure modal is rendered
      setTimeout(() => {
        firstButtonRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="document-type-modal-backdrop" onClick={handleBackdropClick}>
      <div className="document-type-modal">
        <div className="modal-header">
          <h3>Select Document Type</h3>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-content">
          <p className="filename-text">
            Uploading: <strong>{filename}</strong>
          </p>
          
          <div className="document-type-grid">
            {documentTypeOptions.map(({ type, icon: Icon, label, description }, index) => (
              <button
                key={type}
                ref={index === 0 ? firstButtonRef : undefined}
                className="document-type-option"
                onClick={() => onSelect(type)}
              >
                <Icon size={32} />
                <span className="option-label">{label}</span>
                <span className="option-description">{description}</span>
              </button>
            ))}
          </div>

          <div className="modal-footer">
            <button
              className="skip-button"
              onClick={() => onSelect(null)}
            >
              Skip (Press Enter)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentTypeModal;